import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  identificationMenu,
  identificationTypes,
} from './identification_types';
import normalizeImage from './normalize_image';
import { compareFaces, registerProfile } from './api';

// アプリ版（IdentificationScreen.js〜Step4Screen.js）と同等のフローを Web 上で再現する。
// 5 ステップ構成: entry → guide → preview → selfie → profile

const ACADEMIC_YEARS_OPTIONS = [
  { title: '4年制', value: 4 },
  { title: '3年制', value: 3 },
  { title: '2年制', value: 2 },
  { title: '1年制', value: 1 },
];

// 2026 のスタイルガイド（Primary / Neutral / Blue）にそろえた色。
// MAIN_COLOR は以前 '#ff7680' というブランド外の赤だった。
const MAIN_COLOR = '#FF6467';      // Primary 500（ブランドの赤）
const MAIN_COLOR_DARK = '#D15254'; // Primary 700（hover・注意文）
const MAIN_COLOR_DEEP = '#943A3C'; // Primary 900（丸のべた塗り）
const TINT_COLOR = '#FFF2F3';      // Primary 50（うすい赤の面）
const TINT_LINE = '#FFE3E4';       // Primary 100（うすい赤の枠線）
const INK_COLOR = '#1E1E22';       // Neutral 900（本文の黒）
const SUB_COLOR = '#4A4A52';       // Neutral 700
const MUTED_COLOR = '#8A8A93';     // Neutral 500
const LINE_COLOR = '#E8E8EB';      // Neutral 200（区切り線）
const LINE_COLOR_DARK = '#D3D3D8'; // Neutral 300（ステップ間の矢印）
const LINK_COLOR = '#306BCA';      // Blue 700
const WARNING_COLOR = MAIN_COLOR_DARK;

// ボタンは 2026-09-19 に Figma から読み取ったボタン部品の値にそろえる。
// 主役＝公式グラデ／脇役＝Neutral 500 のべた塗り。どちらも 高さ56・角丸12px・16px/700・白文字。
const BUTTON_HEIGHT = 56;
const BUTTON_RADIUS = 12;
const BRAND_GRADIENT = 'linear-gradient(270deg, #FF45B9 0%, #FF7245 100%)';
const BRAND_GRADIENT_SHADOW = '0 7px 18px rgba(255, 100, 103, .26)';

// 撮影のご案内画面と、共通ボタンの見た目。2026 のスタイルガイドにそろえている。
// 文字・色・余白・角丸は改修ずみ画面（地域選択モーダル等）と同じ値を使う。
const IDW_2026_STYLE = `
/* ===== 書類をえらぶモーダル（デザイン 2026-09-23） ===== */
/* 見出しまわりの値は、改修ずみの地域を選ぶモーダル（.pref-2026）に合わせている */
.idw-2026__picker-head {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 20px 25px 16px;
}
.idw-2026__picker-heading { flex: 1; min-width: 0; }
.idw-2026__picker-title {
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  line-height: 1.4;
  letter-spacing: .02em;
  color: ${INK_COLOR};
}
.idw-2026__picker-lead {
  margin: 8px 0 0;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.7;
  color: ${SUB_COLOR};
  word-break: normal;
  overflow-wrap: anywhere;
}
.idw-2026__picker-close {
  flex: none;
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin: -4px -4px 0 0;
  padding: 0;
  background: none;
  border: 0;
  border-radius: 8px;
  color: ${SUB_COLOR};
  cursor: pointer;
}
.idw-2026__picker-close:hover { background: #F7F7F8; color: ${INK_COLOR}; }

/* 1件ずつ独立したカードにして並べる（アプリの新規登録画面と同じ作り）。
   ただし地はグレーではなく白。カードの輪郭は枠線だけで見せる */
.idw-2026__picker-list {
  list-style: none;
  display: grid;
  /* 間隔と余白は地域モーダル（47個のグリッド）ではなく、この画面の中身に合わせる。
     1行に アイコン＋書類名＋注記＋矢印 が入るため、詰めすぎると窮屈になる */
  gap: 12px;
  margin: 0;
  padding: 4px 25px 25px;
  background: #FFFFFF;
}
.idw-2026__picker-item {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 14px;
  min-height: 60px;
  padding: 14px 16px;
  background: #FFFFFF;
  border: 1px solid ${LINE_COLOR};
  border-radius: 8px;
  text-align: left;
  cursor: pointer;
  transition: background-color .15s ease, border-color .15s ease;
}
/* 触れているカードはうすい赤で示す */
.idw-2026__picker-item:hover,
.idw-2026__picker-item:focus-visible {
  background: #F7F7F8;
  border-color: ${LINE_COLOR_DARK};
  outline: none;
}
/* アイコンはうすい赤の角丸タイルに入れる */
.idw-2026__picker-icon {
  flex: none;
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${TINT_LINE};
  border-radius: 8px;
  color: ${MAIN_COLOR};
}
.idw-2026__picker-body { flex: 1; min-width: 0; }
.idw-2026__picker-name {
  display: block;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.4;
  color: ${INK_COLOR};
}
.idw-2026__picker-note {
  display: block;
  margin-top: 2px;
  font-size: 10px;
  font-weight: 400;
  line-height: 1.4;
  color: ${MUTED_COLOR};
  word-break: normal;
  overflow-wrap: anywhere;
}
/* 右端の「→」 */
.idw-2026__picker-arrow {
  flex: none;
  display: inline-flex;
  color: ${INK_COLOR};
}

@media (max-width: 480px) {
  .idw-2026__picker-head { padding: 16px 16px 12px; }
  .idw-2026__picker-list { padding: 4px 16px 16px; }
}

/* ===== 入口の画面（手書きラフ 2026-09-23 の構成） ===== */
.idw-2026__entry {
  max-width: 800px;
  margin: 0 auto;
  padding: 32px 16px 40px;
}
/* ラフに合わせて見出しは中央ぞろえ。ページ名は左ぞろえという既存の決めごとと
   食い違うため、戻したいときはこの2箇所の text-align を left にする */
.idw-2026__entry-title {
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  line-height: 1.4;
  text-align: center;
  color: ${MAIN_COLOR};
}
/* 見出しの下の説明文。アプリ版で説明文が入っている位置に置いている */
.idw-2026__entry-lead {
  /* 2文それぞれが1行に収まる幅。改行は文の切れ目（「安全に管理されます。」の後）に置く */
  max-width: 620px;
  margin: 12px auto 0;
  font-size: 13px;
  font-weight: 400;
  line-height: 1.9;
  text-align: center;
  color: ${SUB_COLOR};
  /* レイアウト共通の break-all を打ち消し、日本語として自然な位置で折り返す */
  word-break: normal;
  overflow-wrap: anywhere;
  line-break: strict;
}
/* 画面が狭いときは、入れた改行を無効にして自然に流す
   （半端な位置で2行目が1語だけ残るのを避ける） */
@media (max-width: 599px) {
  .idw-2026__entry-lead br { display: none; }
  .idw-2026__entry-lead { text-align: left; }
}
.idw-2026__section-title {
  /* 説明文との間。スマホは狭め（パソコンは下の media query で28px） */
  margin: 24px 0 0;
  font-size: 20px;
  font-weight: 700;
  line-height: 1.5;
  text-align: center;
  color: ${SUB_COLOR};
}

/* 本人確認でできること。パソコンは2つ横並び、スマホは縦積み */
/* 「本人確認をすると」。優先順位が低いため、流れのうしろに置いている。
   見出しごと1つのカードに収めて、静かなかたまりとして扱う */
/* 見出しと2つのメリットを、カードと同じグレーの面でひとかたまりにする。
   中のアイテムの位置は変えず、地だけをこの器が持つ */
.idw-2026__benefits-block {
  margin-top: 28px;
  padding: 20px;
  background: #F7F7F8;
  border-radius: 12px;
}
/* 流れの見出しより一段小さく、太さも落として主従をはっきりさせる */
.idw-2026__section-title--sub {
  margin: 0;
  font-size: 16px;
  font-weight: 400;
}

.idw-2026__benefits {
  list-style: none;
  display: grid;
  grid-template-columns: 1fr;
  /* カード同士の間隔はステップと同じ値にそろえる（スマホ・パソコンとも20px） */
  gap: 20px;
  margin: 20px 0 0;
  padding: 0;
}
/* アプリ版の新規登録時の本人確認画面と同じ作り。
   うすいグレーの面・アイコンなし・見出し14px/700＋説明12px のグレー */
.idw-2026__benefit {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  /* グレーの器の中に、白いカードとして置く */
  background: #FFFFFF;
  border: 0;
  border-radius: 10px;
}
/* 左のアイコン。形は書類をえらぶモーダルと同じ角丸タイルだが、色はグレー系。
   いちばん見てほしい「本人確認の流れ」より目立たせないため赤は使わない。
   タイルの地は、外側の器のグレー(#F7F7F8)と同じ色にそろえている */
.idw-2026__benefit-badge {
  flex: none;
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #F7F7F8;
  border-radius: 8px;
  color: ${SUB_COLOR};
}
.idw-2026__benefit-body { display: block; min-width: 0; }
.idw-2026__benefit-title {
  display: block;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.5;
  color: ${INK_COLOR};
}
.idw-2026__benefit-text {
  display: block;
  margin-top: 4px;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.6;
  color: ${SUB_COLOR};
  word-break: normal;
  overflow-wrap: anywhere;
}

/* 本人確認の流れ。パソコンは3つ横並び、スマホは縦積み */
.idw-2026__steps {
  list-style: none;
  display: grid;
  grid-template-columns: 1fr;
  /* スマホはカードが縦に積まれ、すき間に「＞」が入る。
     20pxだと矢印とカードが近く見えるため、ここだけ広げている（パソコンは20px） */
  gap: 28px;
  margin: 20px 0 0;
  padding: 0;
}
/* ステップ: 地は Primary 50 のうすい赤。札を濃い赤にして主役を示す */
.idw-2026__step-card {
  position: relative;
  padding: 20px;
  background: ${TINT_COLOR};
  border: 0;
  border-radius: 12px;
}
/* カードとカードの間に置く「＞」。順番に進むことを示す飾りなので、
   文字ではなく線で描いている（読み上げの対象にしない） */
.idw-2026__step-card:not(:last-child)::after {
  content: '';
  position: absolute;
  width: 16px;
  height: 16px;
  border-top: 3px solid ${LINE_COLOR_DARK};
  border-right: 3px solid ${LINE_COLOR_DARK};
  /* スマホ: カードが縦に積まれるので、下向きにしてすき間(12px)の中央に置く。
     四角を回して描いているため、回したあとの見た目の中心は四角の中心と
     ずれる（右下に寄る）。そのぶんを差し引いた値にして、カードに重ならない
     ようにしている */
  left: 50%;
  top: 100%;
  transform: translate(-50%, -50%) translateY(8px) rotate(135deg);
}
/* 札は Primary main のべた塗りに白抜き */
.idw-2026__step-label {
  display: inline-block;
  padding: 4px 12px;
  background: ${MAIN_COLOR};
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: .06em;
  color: #FFFFFF;
}
.idw-2026__step-title {
  margin: 12px 0 0;
  font-size: 16px;
  font-weight: 700;
  line-height: 1.5;
  color: ${INK_COLOR};
}
/* 使える身分証の名前。ここだけ太字にして拾いやすくする */
.idw-2026__step-strong { font-weight: 700; }
.idw-2026__step-text {
  margin: 6px 0 0;
  font-size: 13px;
  font-weight: 400;
  line-height: 1.8;
  color: ${SUB_COLOR};
  word-break: normal;
  overflow-wrap: anywhere;
}

.idw-2026__entry-action { margin-top: 32px; }

@media (min-width: 768px) {
  .idw-2026__entry { padding: 48px 16px 56px; }
  .idw-2026__entry-title { font-size: 28px; }
  .idw-2026__benefits { grid-template-columns: repeat(2, 1fr); gap: 20px; }
  .idw-2026__steps { grid-template-columns: repeat(3, 1fr); gap: 20px; }

  /* ===== パソコンだけ中身を締める =====
     横に並ぶぶん1枚あたりの中身が少なく、縦に間延びして見えるため、
     かたまりの間隔と余白を一段詰める。モバイルの値は変えていない。 */
  .idw-2026__entry { padding: 40px 16px 48px; }
  .idw-2026__entry-lead { margin-top: 10px; line-height: 1.8; }
  .idw-2026__section-title { margin-top: 28px; }
  /* 上の1行は「本人確認の流れ」用。あとに書いてあるぶん強く効くので、
     器の中に入っている「本人確認をすると」には効かないよう打ち消す */
  .idw-2026__section-title--sub { margin-top: 0; }
  .idw-2026__steps { margin-top: 16px; }
  .idw-2026__benefits-block { margin-top: 14px; padding: 18px 20px; }
  .idw-2026__benefits { margin-top: 14px; }
  .idw-2026__entry-action { margin-top: 28px; }
  /* カードの中も詰める。STEP3のように説明が短い枚は余白が余って見えるため */
  .idw-2026__step-card { padding: 18px; }
  .idw-2026__step-title { margin-top: 10px; }
  .idw-2026__step-text { margin-top: 5px; line-height: 1.7; }
  /* パソコン: カードが横に並ぶので、右向きにしてすき間(16px)の中央に置く */
  .idw-2026__step-card:not(:last-child)::after {
    left: 100%;
    top: 50%;
    transform: translate(-50%, -50%) translateX(6px) rotate(45deg);
  }
  /* ボタンは横いっぱいに伸ばさず、中央に置く */
  .idw-2026__entry-action { max-width: 360px; margin-left: auto; margin-right: auto; }
}

.idw-2026__step {
  max-width: 560px;
  margin: 0 auto;
  padding: 32px 16px 40px;
}
/* 以前は上半分に min-height:70vh を持たせていたため、パソコンでは説明とボタンの間に
   400px 前後の空白ができていた。中身の分だけの高さにして、その空白をなくしている。 */
.idw-2026__main { padding-bottom: 28px; }

/* ページの名前。左ぞろえ・ブランドの赤。書体はサイト共通の Noto Sans JP */
.idw-2026__title {
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  line-height: 1.4;
  color: ${MAIN_COLOR};
}
@media (min-width: 768px) {
  .idw-2026__step { padding: 48px 16px 56px; }
  .idw-2026__title { font-size: 28px; }
}

/* 書類ごとの注意書き。絵文字をやめて図形のアイコンにしている */
.idw-2026__caution {
  display: flex;
  gap: 8px;
  margin: 16px 0 0;
  font-size: 13px;
  font-weight: 400;
  line-height: 1.7;
  color: ${WARNING_COLOR};
  word-break: normal;
  overflow-wrap: anywhere;
}
.idw-2026__caution-icon { flex: none; margin-top: 3px; }

/* 安心してもらうための囲み */
.idw-2026__assure {
  margin-top: 28px;
  padding: 20px;
  background: ${TINT_COLOR};
  border: 1px solid ${TINT_LINE};
  border-radius: 12px;
}
.idw-2026__assure-title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  line-height: 1.5;
  color: ${INK_COLOR};
}
.idw-2026__assure-icon { flex: none; color: ${MAIN_COLOR}; }
.idw-2026__assure-text {
  margin: 10px 0 0;
  font-size: 13px;
  font-weight: 400;
  line-height: 1.8;
  color: ${SUB_COLOR};
  word-break: normal;
  overflow-wrap: anywhere;
}

/* 下段（規約の確認とボタン）。区切り線は基準のうすいグレー1本だけ */
.idw-2026__foot {
  padding-top: 24px;
  border-top: 1px solid ${LINE_COLOR};
}
.idw-2026__terms {
  margin: 0;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.8;
  color: ${MUTED_COLOR};
  word-break: normal;
  overflow-wrap: anywhere;
}
.idw-2026__link {
  color: ${LINK_COLOR};
  font-weight: 700;
  text-decoration: none;
}
.idw-2026__link:hover {
  color: ${LINK_COLOR};
  text-decoration: underline;
  text-underline-offset: 3px;
}

.idw-2026__actions {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 20px;
}
.idw-2026__back-wrap { margin-top: 16px; text-align: center; }
.idw-2026__back {
  background: none;
  border: 0;
  padding: 8px 12px;
  font-size: 14px;
  font-weight: 700;
  color: ${MUTED_COLOR};
  cursor: pointer;
}
.idw-2026__back:hover { color: ${INK_COLOR}; text-decoration: underline; text-underline-offset: 3px; }

/* 共通ボタンの動きだけを足す（大きさ・色は各コンポーネント側の指定） */
.idw-2026__btn { transition: filter .15s ease, background-color .15s ease, box-shadow .15s ease; }
.idw-2026__btn--primary:hover:not(:disabled) { filter: brightness(1.06); }
.idw-2026__btn--outline:hover { background-color: ${SUB_COLOR} !important; }
`;

const buildProceedUrl = (redirectUrl, fallbackUrl) => {
  if (!redirectUrl) {
    return fallbackUrl;
  }
  try {
    const url = new URL(redirectUrl, window.location.origin);
    // アプリ版踏襲: WebView 等で完了後にリロードさせるための適当なパラメータ
    url.searchParams.set('__rat', `${Date.now()}`);
    return url.toString();
  } catch (_) {
    return fallbackUrl;
  }
};

// 単純なモーダル（Bootstrap JS に依存しない）
const Modal = ({ isOpen, onClose, children, dismissable = true }) => {
  if (!isOpen) {
    return null;
  }
  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1050,
        padding: 16,
      }}
      onClick={dismissable ? onClose : undefined}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: '#fff',
          borderRadius: 15,
          maxWidth: 480,
          width: '100%',
          maxHeight: '90vh',
          overflowY: 'auto',
        }}
      >
        {children}
      </div>
    </div>
  );
};

Modal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  children: PropTypes.node,
  dismissable: PropTypes.bool,
};

const AlertModal = ({ alert, onDismiss }) => (
  <Modal isOpen={!!alert} onClose={onDismiss} dismissable={false}>
    {alert && (
      <div style={{ padding: 24 }}>
        {alert.title && (
          <div style={{ fontSize: 16, fontWeight: 'bold', marginBottom: 12 }}>
            {alert.title}
          </div>
        )}
        {alert.message && (
          <div style={{ fontSize: 14, marginBottom: 24, whiteSpace: 'pre-wrap' }}>
            {alert.message}
          </div>
        )}
        <div style={{ textAlign: 'right' }}>
          <button
            type="button"
            className="btn"
            style={{ color: MAIN_COLOR, fontWeight: 'bold' }}
            onClick={onDismiss}
          >
            OK
          </button>
        </div>
      </div>
    )}
  </Modal>
);

AlertModal.propTypes = {
  alert: PropTypes.shape({
    title: PropTypes.string,
    message: PropTypes.string,
  }),
  onDismiss: PropTypes.func.isRequired,
};

const LoadingOverlay = ({ message }) => (
  <Modal isOpen dismissable={false}>
    <div style={{ padding: 32, textAlign: 'center' }}>
      <div
        className="spinner-border"
        role="status"
        style={{ color: MAIN_COLOR, marginBottom: 16 }}
      >
        <span className="sr-only">Loading...</span>
      </div>
      <div style={{ fontSize: 14, fontWeight: 'bold' }}>{message}</div>
    </div>
  </Modal>
);

LoadingOverlay.propTypes = {
  message: PropTypes.string.isRequired,
};

// 主役ボタン。Figma のボタン部品にそろえて 公式グラデ / 高さ56 / 角丸12 / 16px・700。
// 押せないときはグラデをやめて Neutral 300 のべた塗りにする。
const PrimaryButton = ({ disabled, onClick, children, type = 'button', style }) => (
  <button
    type={type}
    onClick={onClick}
    disabled={disabled}
    className="idw-2026__btn idw-2026__btn--primary"
    style={{
      width: '100%',
      height: BUTTON_HEIGHT,
      borderRadius: BUTTON_RADIUS,
      border: 'none',
      color: '#fff',
      fontWeight: 700,
      fontSize: 16,
      letterSpacing: '.02em',
      background: disabled ? '#D3D3D8' : BRAND_GRADIENT,
      boxShadow: disabled ? 'none' : BRAND_GRADIENT_SHADOW,
      cursor: disabled ? 'not-allowed' : 'pointer',
      ...style,
    }}
  >
    {children}
  </button>
);

PrimaryButton.propTypes = {
  disabled: PropTypes.bool,
  onClick: PropTypes.func,
  children: PropTypes.node,
  type: PropTypes.string,
  style: PropTypes.object,
};

// 脇役ボタン。Figma のボタン部品にそろえて Neutral 500 のべた塗り＋白文字。
// 大きさは主役と同じ。影は付けず、色の濃さだけで主従をつける。
const OutlineButton = ({ onClick, children, style }) => (
  <button
    type="button"
    onClick={onClick}
    className="idw-2026__btn idw-2026__btn--outline"
    style={{
      width: '100%',
      height: BUTTON_HEIGHT,
      borderRadius: BUTTON_RADIUS,
      border: 'none',
      color: '#fff',
      fontWeight: 700,
      fontSize: 16,
      letterSpacing: '.02em',
      backgroundColor: MUTED_COLOR,
      cursor: 'pointer',
      ...style,
    }}
  >
    {children}
  </button>
);

OutlineButton.propTypes = {
  onClick: PropTypes.func,
  children: PropTypes.node,
  style: PropTypes.object,
};

// ファイル input をプログラム的にトリガーするフック
// camera = 'environment' で背面カメラ, 'user' で前面カメラ
// camera = null/false なら capture 属性を付けず、カメラ/ライブラリ選択を可能にする (TGW-1723)
const useFileCapture = (camera, onPick) => {
  const inputRef = useRef(null);

  const handleChange = (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (file) {
      onPick(file);
    }
  };

  const trigger = useCallback(() => {
    if (inputRef.current) {
      inputRef.current.click();
    }
  }, []);

  const input = (
    <input
      ref={inputRef}
      type="file"
      accept="image/*"
      capture={camera || undefined}
      onChange={handleChange}
      style={{ display: 'none' }}
    />
  );

  return { trigger, input };
};

// 入口の画面。デザイナーの手書きラフ（2026-09-23）の構成に合わせている。
// 見出し →「本人確認でできること」2つ →「本人確認の流れ」3ステップ → ボタン。
const ENTRY_BENEFITS = [
  {
    key: 'badge',
    title: '本人確認済みマーク',
    text: '参加者や主催者へ安心感を伝えられます',
    // 盾にチェック＝確認ずみの印
    icon: (
      <>
        <path d="M12 2 4 5v6c0 5.5 3.4 9.6 8 11 4.6-1.4 8-5.5 8-11V5l-8-3Z" />
        <path d="m9 11.5 2 2 4-4" />
      </>
    ),
  },
  {
    key: 'private',
    title: '確認情報は非公開',
    text: '本人確認で取得した情報は、ほかのユーザーには公開されません。',
    // 鍵＝非公開
    icon: (
      <>
        <rect x="4" y="10" width="16" height="11" rx="2" />
        <path d="M8 10V7a4 4 0 0 1 8 0v3" />
      </>
    ),
  },
];

const ENTRY_STEPS = [
  {
    key: 'document',
    label: 'STEP1',
    title: '身分証撮影',
    // 使える身分証の名前だけ太字にするため、文を2つに分けている
    strong: '免許証・マイナンバーカード・在留カード・パスポート・学生証',
    text: 'がご利用いただけます。',
  },
  {
    key: 'selfie',
    label: 'STEP2',
    title: '自撮り撮影',
    text: 'おひとりで、明るい場所で、お顔が大きく写るように撮影してください。',
  },
  {
    key: 'profile',
    label: 'STEP3',
    title: '個人情報入力',
    text: '身分証に記載されているお名前を入力します。',
  },
];

const EntryStep = ({ onStart }) => (
  <div className="idw-2026__entry">
    <h1 className="idw-2026__entry-title">身分証による本人確認</h1>
    <p className="idw-2026__entry-lead">
      本人確認のため、本人確認書類と顔写真をスマートフォンまたはパソコンで撮影します。
      <br />
      取得した個人情報は本人確認のために使用し、ほかのユーザーに公開されることはありません。
    </p>

    <h2 className="idw-2026__section-title">本人確認の流れ</h2>
    <ol className="idw-2026__steps">
      {ENTRY_STEPS.map((step) => (
        <li key={step.key} className="idw-2026__step-card">
          <span className="idw-2026__step-label">{step.label}</span>
          <p className="idw-2026__step-title">{step.title}</p>
          <p className="idw-2026__step-text">
            {step.strong && <b className="idw-2026__step-strong">{step.strong}</b>}
            {step.text}
          </p>
        </li>
      ))}
    </ol>

    <div className="idw-2026__benefits-block">
      <h2 className="idw-2026__section-title idw-2026__section-title--sub">本人確認をすると</h2>
      <ul className="idw-2026__benefits">
      {ENTRY_BENEFITS.map((benefit) => (
        <li key={benefit.key} className="idw-2026__benefit">
          <span className="idw-2026__benefit-badge" aria-hidden="true">
            <svg
              width="19"
              height="19"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              {benefit.icon}
            </svg>
          </span>
          <span className="idw-2026__benefit-body">
            <span className="idw-2026__benefit-title">{benefit.title}</span>
            <span className="idw-2026__benefit-text">{benefit.text}</span>
          </span>
        </li>
        ))}
      </ul>
    </div>

    <div className="idw-2026__entry-action">
      <PrimaryButton onClick={onStart}>同意して本人確認する</PrimaryButton>
    </div>
  </div>
);

EntryStep.propTypes = {
  onStart: PropTypes.func.isRequired,
};

// 書類ごとのアイコン（線画）。デザイン（2026-09-23 受領）にならって左に置く。
const DOCUMENT_ICONS = {
  driverLicense: (
    <>
      <path d="M4.5 16.5v1.8a.7.7 0 0 1-.7.7H3a.7.7 0 0 1-.7-.7v-1.8" />
      <path d="M21.7 16.5v1.8a.7.7 0 0 1-.7.7h-.8a.7.7 0 0 1-.7-.7v-1.8" />
      <path d="M3.2 11.5 5 6.6A1.6 1.6 0 0 1 6.5 5.5h11A1.6 1.6 0 0 1 19 6.6l1.8 4.9" />
      <rect x="2.3" y="11.5" width="19.4" height="5" rx="1.6" />
      <circle cx="6.3" cy="14" r="1" />
      <circle cx="17.7" cy="14" r="1" />
    </>
  ),
  myNumberCard: (
    <>
      <rect x="2" y="5" width="20" height="14" rx="2.5" />
      <rect x="13.5" y="8.5" width="5.5" height="7" rx="1" />
      <line x1="5" y1="10" x2="10.5" y2="10" />
      <line x1="5" y1="13.5" x2="10.5" y2="13.5" />
    </>
  ),
  residenceCard: (
    <>
      <rect x="2" y="5" width="20" height="14" rx="2.5" />
      <circle cx="8" cy="11" r="2" />
      <path d="M4.8 16.2c.5-1.6 1.7-2.4 3.2-2.4s2.7.8 3.2 2.4" />
      <line x1="14.5" y1="10.5" x2="19" y2="10.5" />
      <line x1="14.5" y1="14" x2="17" y2="14" />
    </>
  ),
  passport: (
    <>
      <rect x="4" y="2.5" width="16" height="19" rx="2.2" />
      <circle cx="12" cy="10" r="3.2" />
      <path d="M8.8 10h6.4M12 6.8c1.5 1.9 1.5 4.5 0 6.4-1.5-1.9-1.5-4.5 0-6.4z" />
      <line x1="9" y1="17.5" x2="15" y2="17.5" />
    </>
  ),
  studentCard: (
    <>
      <path d="M2.5 9 12 4.5 21.5 9 12 13.5z" />
      <path d="M6.5 11.2V15c0 1.4 2.5 2.5 5.5 2.5s5.5-1.1 5.5-2.5v-3.8" />
    </>
  ),
};

// 書類ごとの注意書き。以前はモーダルの下にまとめて置いていたが、
// どの書類の話か分かるよう、それぞれの行の下へ移した。
const DOCUMENT_NOTES = {
  passport: '2020年2月以降に発行されたパスポートは利用できません',
  studentCard: '年度末時点で23歳以下の大学・短大・専門学校生が利用できます',
};

const DocumentPickerModal = ({ isOpen, onClose, onSelect }) => (
  <Modal isOpen={isOpen} onClose={onClose}>
    <div className="idw-2026__picker">
      <div className="idw-2026__picker-head">
        <div className="idw-2026__picker-heading">
          <h2 className="idw-2026__picker-title">本人確認書類を選択</h2>
          <p className="idw-2026__picker-lead">書類を選択後、本人確認書類と顔写真を撮影します。</p>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="閉じる"
          className="idw-2026__picker-close"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            aria-hidden="true"
          >
            <path d="M18 6 6 18" />
            <path d="m6 6 12 12" />
          </svg>
        </button>
      </div>

      <ul className="idw-2026__picker-list">
        {identificationMenu.map((item) => (
          <li key={item.type}>
            <button
              type="button"
              onClick={() => onSelect(item.type)}
              className="idw-2026__picker-item"
            >
              <span className="idw-2026__picker-icon" aria-hidden="true">
                <svg
                  width="19"
                  height="19"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  {DOCUMENT_ICONS[item.type]}
                </svg>
              </span>
              <span className="idw-2026__picker-body">
                <span className="idw-2026__picker-name">{item.title}</span>
                {DOCUMENT_NOTES[item.type] && (
                  <span className="idw-2026__picker-note">{DOCUMENT_NOTES[item.type]}</span>
                )}
              </span>
              <span className="idw-2026__picker-arrow" aria-hidden="true">
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M4 12h15" />
                  <path d="m13 6 6 6-6 6" />
                </svg>
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  </Modal>
);

DocumentPickerModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
};

const GuideStep = ({ type, rulesUrl, privacyPolicyUrl, onProceedCamera, onProceedLibrary, onBack }) => {
  const meta = identificationTypes[type];
  return (
    <div className="idw-2026__step">
      <div className="idw-2026__main">
        <h2 className="idw-2026__title">{meta.name}をご用意ください</h2>
        {meta.alert && (
          <p className="idw-2026__caution">
            <svg
              className="idw-2026__caution-icon"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
            <span>{meta.alert}</span>
          </p>
        )}

        {/* 安心してもらうための一言。うすい赤の面に置き、盾は見出しの横に並べる
            （以前は枠線の上に白い四角を重ねていて、枠線が透けて見えていた） */}
        <div className="idw-2026__assure">
          <p className="idw-2026__assure-title">
            <svg
              className="idw-2026__assure-icon"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" />
            </svg>
            お客さまの本人情報を守ります
          </p>
          <p className="idw-2026__assure-text">
            お客さまからいただいた個人情報はつなげーとが大切に管理し、本人確認以外には使いません。
          </p>
        </div>
      </div>

      <div className="idw-2026__foot">
        <p className="idw-2026__terms">
          <a href={rulesUrl} target="_blank" rel="noreferrer" className="idw-2026__link">
            つなげーと利用規約
          </a>
          および
          <a href={privacyPolicyUrl} target="_blank" rel="noreferrer" className="idw-2026__link">
            プライバシーと情報セキュリティの方針
          </a>
          をご確認いただき、内容に同意した上で次へお進みください。
        </p>
        {/* 並列な2択（カメラ/フォルダ）を縦に並べ、階層の違う「戻る」は
            テキストリンクとして最下段に置く */}
        <div className="idw-2026__actions">
          <PrimaryButton onClick={onProceedCamera}>同意してカメラで撮影</PrimaryButton>
          <OutlineButton onClick={onProceedLibrary}>同意してフォルダから選択</OutlineButton>
        </div>
        <div className="idw-2026__back-wrap">
          <button type="button" onClick={onBack} className="idw-2026__back">
            戻る
          </button>
        </div>
      </div>
    </div>
  );
};

GuideStep.propTypes = {
  type: PropTypes.string.isRequired,
  rulesUrl: PropTypes.string.isRequired,
  privacyPolicyUrl: PropTypes.string.isRequired,
  onProceedCamera: PropTypes.func.isRequired,
  onProceedLibrary: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
};

const PreviewStep = ({ type, frontImage, backImage, onRetake, onProceed }) => {
  const meta = identificationTypes[type];
  const frontUrl = useMemo(() => (frontImage ? URL.createObjectURL(frontImage) : null), [frontImage]);
  const backUrl = useMemo(() => (backImage ? URL.createObjectURL(backImage) : null), [backImage]);

  useEffect(() => () => {
    if (frontUrl) URL.revokeObjectURL(frontUrl);
    if (backUrl) URL.revokeObjectURL(backUrl);
  }, [frontUrl, backUrl]);

  return (
    <div style={{ paddingBottom: 96, maxWidth: 560, margin: '0 auto' }}>
      <div style={{ padding: 16, textAlign: 'center' }}>
        <p style={{ fontSize: 18, fontWeight: 'bold', marginTop: 16 }}>{meta.frontLabel}</p>
        {frontUrl && (
          <img
            src={frontUrl}
            alt={meta.frontLabel}
            style={{ maxWidth: '100%', height: 'auto' }}
          />
        )}
        {meta.useBack && (
          <>
            <p style={{ fontSize: 18, fontWeight: 'bold', marginTop: 24 }}>{meta.backLabel}</p>
            {backUrl && (
              <img
                src={backUrl}
                alt={meta.backLabel}
                style={{ maxWidth: '100%', height: 'auto' }}
              />
            )}
          </>
        )}
      </div>
      <div
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          padding: 16,
          backgroundColor: '#fff',
          borderTop: '1px solid rgba(0, 0, 0, 0.12)',
          display: 'flex',
          gap: 8,
          maxWidth: 560,
          margin: '0 auto',
        }}
      >
        <div style={{ flex: 1 }}>
          <OutlineButton onClick={onRetake}>再撮影</OutlineButton>
        </div>
        <div style={{ flex: 1 }}>
          <PrimaryButton onClick={onProceed}>確認して次へ</PrimaryButton>
        </div>
      </div>
    </div>
  );
};

PreviewStep.propTypes = {
  type: PropTypes.string.isRequired,
  frontImage: PropTypes.object.isRequired,
  backImage: PropTypes.object,
  onRetake: PropTypes.func.isRequired,
  onProceed: PropTypes.func.isRequired,
};

const FormRow = ({ label, children }) => (
  <div
    style={{
      display: 'flex',
      backgroundColor: '#fff',
      borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
      alignItems: 'center',
    }}
  >
    <label style={{ fontSize: 16, fontWeight: 'bold', padding: 16, margin: 0, minWidth: 130 }}>
      {label}
    </label>
    <div style={{ flex: 1, padding: '8px 16px' }}>{children}</div>
  </div>
);

FormRow.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node,
};

const ProfileStep = ({ type, isSubmitting, onSubmit }) => {
  const isStudent = type === 'studentCard';

  const [familyName, setFamilyName] = useState('');
  const [firstName, setFirstName] = useState('');
  const [familyNameKana, setFamilyNameKana] = useState('');
  const [firstNameKana, setFirstNameKana] = useState('');
  const [isFullNameHidden, setIsFullNameHidden] = useState(false);

  const [schoolName, setSchoolName] = useState('');
  const [birthdate, setBirthdate] = useState('');
  const [enrolledAt, setEnrolledAt] = useState('');
  const [academicYears, setAcademicYears] = useState(4);

  const isValidProfile = familyName && firstName && familyNameKana && firstNameKana;
  const isValidStudent = !isStudent || (schoolName && birthdate && enrolledAt && academicYears);
  const isValid = isValidProfile && isValidStudent && !isSubmitting;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isValid) {
      return;
    }
    onSubmit({
      familyName,
      firstName,
      familyNameKana,
      firstNameKana,
      isFullNameHidden,
      student: isStudent
        ? {
            schoolName,
            // バックエンドの想定 (アプリ版踏襲): 'YYYY-MM-DD HH:mm:ss' (Asia/Tokyo)
            birthdate: `${birthdate} 00:00:00`,
            enrolledAt: `${enrolledAt} 00:00:00`,
            academicYears,
          }
        : null,
    });
  };

  return (
    <form onSubmit={handleSubmit} style={{ paddingBottom: 32, maxWidth: 560, margin: '0 auto' }}>
      <div style={{ backgroundColor: '#F5F5F5', padding: 18, margin: 16 }}>
        <p style={{ color: WARNING_COLOR, fontSize: 16, fontWeight: 'bold', margin: 0 }}>
          撮影した本人確認書類の記載と同じ表記で入力してください
        </p>
      </div>
      <div style={{ margin: '0 16px' }}>
        <p style={{ fontSize: 12, margin: 0 }}>
          ※本人確認書類の表記がローマ字の場合は、ローマ字で姓名を入力してください。
        </p>
        <p style={{ fontSize: 12, margin: 0 }}>
          ※本人確認書類の表記が新姓と旧姓で併記されている場合は、新姓のみを入力してください。
        </p>
      </div>

      <div style={{ marginTop: 16 }}>
        <FormRow label="姓（全角）">
          <input
            type="text"
            value={familyName}
            onChange={(e) => setFamilyName(e.target.value)}
            placeholder="例）山田"
            className="form-control"
            style={{ border: 'none', boxShadow: 'none', padding: 0 }}
          />
        </FormRow>
        <FormRow label="名（全角）">
          <input
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            placeholder="例）花子"
            className="form-control"
            style={{ border: 'none', boxShadow: 'none', padding: 0 }}
          />
        </FormRow>
        <FormRow label="姓カナ（全角）">
          <input
            type="text"
            value={familyNameKana}
            onChange={(e) => setFamilyNameKana(e.target.value)}
            placeholder="例）ヤマダ"
            className="form-control"
            style={{ border: 'none', boxShadow: 'none', padding: 0 }}
          />
        </FormRow>
        <FormRow label="名カナ（全角）">
          <input
            type="text"
            value={firstNameKana}
            onChange={(e) => setFirstNameKana(e.target.value)}
            placeholder="例）ハナコ"
            className="form-control"
            style={{ border: 'none', boxShadow: 'none', padding: 0 }}
          />
        </FormRow>
      </div>

      {isStudent && (
        <>
          <div style={{ padding: '24px 16px 8px' }}>
            <p style={{ fontWeight: 'bold', margin: 0 }}>学生情報</p>
          </div>
          <FormRow label="学校名">
            <input
              type="text"
              value={schoolName}
              onChange={(e) => setSchoolName(e.target.value)}
              placeholder="つなげーと大学"
              className="form-control"
              style={{ border: 'none', boxShadow: 'none', padding: 0 }}
            />
          </FormRow>
          <FormRow label="生年月日">
            <input
              type="date"
              value={birthdate}
              onChange={(e) => setBirthdate(e.target.value)}
              className="form-control"
              style={{ border: 'none', boxShadow: 'none', padding: 0 }}
            />
          </FormRow>
          <FormRow label="入学年月日">
            <input
              type="date"
              value={enrolledAt}
              onChange={(e) => setEnrolledAt(e.target.value)}
              className="form-control"
              style={{ border: 'none', boxShadow: 'none', padding: 0 }}
            />
          </FormRow>
          <FormRow label="学業年数">
            <select
              value={academicYears}
              onChange={(e) => setAcademicYears(Number(e.target.value))}
              className="form-control"
              style={{ border: 'none', boxShadow: 'none', padding: 0 }}
            >
              {ACADEMIC_YEARS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.title}
                </option>
              ))}
            </select>
          </FormRow>
        </>
      )}

      <div style={{ padding: '24px 24px' }}>
        <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', margin: 0 }}>
          <input
            type="checkbox"
            checked={isFullNameHidden}
            onChange={(e) => setIsFullNameHidden(e.target.checked)}
            style={{ marginRight: 8 }}
          />
          <span>氏名を他のユーザーに表示しない</span>
        </label>
      </div>

      <div style={{ padding: '0 16px 24px' }}>
        <PrimaryButton type="submit" disabled={!isValid}>
          {isSubmitting ? '送信中...' : '次へ'}
        </PrimaryButton>
      </div>
    </form>
  );
};

ProfileStep.propTypes = {
  type: PropTypes.string.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

const IdentificationWizard = ({
  redirectUrl,
  rulesUrl,
  privacyPolicyUrl,
  homeUrl,
}) => {
  // フロー状態
  const [step, setStep] = useState('entry'); // entry|guide|preview|selfie|profile
  const [isPickingDocument, setIsPickingDocument] = useState(false);
  const [type, setType] = useState(null);
  const [frontImage, setFrontImage] = useState(null);
  const [backImage, setBackImage] = useState(null);

  // ステップ間の通知用 alert
  const [alert, setAlert] = useState(null);
  const [pendingAfterAlert, setPendingAfterAlert] = useState(null);

  // フェーズフラグ
  const [isProcessingImage, setIsProcessingImage] = useState(false);
  const [isVerifyingFace, setIsVerifyingFace] = useState(false);
  const [isSubmittingProfile, setIsSubmittingProfile] = useState(false);

  const isSubmittingProfileRef = useRef(false);

  // ファイル input トリガー
  const onFrontPicked = useCallback(async (file) => {
    setIsProcessingImage(true);
    try {
      const normalized = await normalizeImage(file, 'front.jpg');
      setFrontImage(normalized);
      const meta = identificationTypes[type];
      if (meta && meta.useBack) {
        // 続けて裏面撮影をトリガー（次の useEffect で起動するためのフラグは持たず、
        // ボタン押下フローで戻ってくる方式にする）
        setStep('guide-back');
      } else {
        setStep('preview');
      }
    } catch (e) {
      setAlert({ title: '画像の読み込みに失敗しました', message: '別の画像で再度お試しください。' });
    } finally {
      setIsProcessingImage(false);
    }
  }, [type]);

  const onBackPicked = useCallback(async (file) => {
    setIsProcessingImage(true);
    try {
      const normalized = await normalizeImage(file, 'back.jpg');
      setBackImage(normalized);
      setStep('preview');
    } catch (e) {
      setAlert({ title: '画像の読み込みに失敗しました', message: '別の画像で再度お試しください。' });
    } finally {
      setIsProcessingImage(false);
    }
  }, []);

  const onSelfiePicked = useCallback(async (file) => {
    setIsProcessingImage(true);
    let selfie = null;
    try {
      selfie = await normalizeImage(file, 'selfie.jpg');
    } catch (e) {
      setIsProcessingImage(false);
      setAlert({ title: '画像の読み込みに失敗しました', message: '別の画像で再度お試しください。' });
      return;
    }
    setIsProcessingImage(false);

    setIsVerifyingFace(true);
    try {
      const response = await compareFaces({
        frontImage,
        backImage,
        selfieImage: selfie,
      });
      setIsVerifyingFace(false);

      if (response.alert) {
        const next = response.success ? () => setStep('profile') : null;
        setAlert(response.alert);
        setPendingAfterAlert(() => next);
      } else if (response.success) {
        setStep('profile');
      } else {
        setAlert({
          title: '本人確認ができません',
          message: '再度、カメラでお顔の撮影を行ってください。',
        });
      }
    } catch (e) {
      setIsVerifyingFace(false);
      setAlert({
        title: '本人確認ができません',
        message: '再度、カメラでお顔の撮影を行ってください。',
      });
    }
  }, [frontImage, backImage]);

  // ファイル input
  // 身分証はカメラ撮影とフォルダ選択の両方を許可し、ボタンで出し分ける。
  // セルフィーはカメラ限定を維持(偽装対策)。capture はモバイルのみ有効で、PCではファイル選択になる
  const frontCamera = useFileCapture('environment', onFrontPicked);
  const frontLibrary = useFileCapture(null, onFrontPicked);
  const backCamera = useFileCapture('environment', onBackPicked);
  const backLibrary = useFileCapture(null, onBackPicked);
  const selfie = useFileCapture('user', onSelfiePicked);

  const startFlow = useCallback(() => {
    setIsPickingDocument(true);
  }, []);

  const handleDocumentSelect = useCallback((selectedType) => {
    setType(selectedType);
    setIsPickingDocument(false);
    setFrontImage(null);
    setBackImage(null);
    setStep('guide');
  }, []);

  const handleAlertDismiss = useCallback(() => {
    const cb = pendingAfterAlert;
    setAlert(null);
    setPendingAfterAlert(null);
    if (typeof cb === 'function') {
      cb();
    }
  }, [pendingAfterAlert]);

  const handleProfileSubmit = useCallback(async (values) => {
    if (isSubmittingProfileRef.current) {
      return;
    }
    isSubmittingProfileRef.current = true;
    setIsSubmittingProfile(true);

    try {
      const response = await registerProfile(values);
      const success = response.success;

      const proceedOnSuccess = () => {
        const dest = buildProceedUrl(redirectUrl, homeUrl);
        window.location.assign(dest);
      };

      if (response.alert) {
        if (success) {
          setAlert(response.alert);
          setPendingAfterAlert(() => proceedOnSuccess);
        } else {
          setAlert(response.alert);
          isSubmittingProfileRef.current = false;
          setIsSubmittingProfile(false);
        }
      } else if (success) {
        proceedOnSuccess();
      } else {
        setAlert({
          title: '本人情報の登録に失敗しました',
          message: 'しばらく時間をおいてから再度お試しください。',
        });
        isSubmittingProfileRef.current = false;
        setIsSubmittingProfile(false);
      }
    } catch (e) {
      setAlert({
        title: '本人情報の登録に失敗しました',
        message: 'しばらく時間をおいてから再度お試しください。',
      });
      isSubmittingProfileRef.current = false;
      setIsSubmittingProfile(false);
    }
  }, [redirectUrl, homeUrl]);

  // ステップ別のレンダリング
  let body = null;
  if (step === 'entry') {
    body = (
      <EntryStep onStart={startFlow} />
    );
  } else if (step === 'guide') {
    body = (
      <GuideStep
        type={type}
        rulesUrl={rulesUrl}
        privacyPolicyUrl={privacyPolicyUrl}
        onProceedCamera={() => frontCamera.trigger()}
        onProceedLibrary={() => frontLibrary.trigger()}
        onBack={() => {
          setType(null);
          setStep('entry');
        }}
      />
    );
  } else if (step === 'guide-back') {
    // 裏面撮影の案内 + 起動
    body = (
      <div style={{ padding: 36, textAlign: 'center', maxWidth: 560, margin: '0 auto' }}>
        <h2 style={{ fontSize: 18, fontWeight: 'bold' }}>
          {identificationTypes[type].name}の{identificationTypes[type].backLabel}を撮影してください
        </h2>
        <div style={{ marginTop: 24 }}>
          <PrimaryButton onClick={() => backCamera.trigger()}>裏面をカメラで撮影</PrimaryButton>
        </div>
        <div style={{ marginTop: 12 }}>
          <OutlineButton onClick={() => backLibrary.trigger()}>フォルダから選択</OutlineButton>
        </div>
        <div style={{ marginTop: 12 }}>
          <OutlineButton
            onClick={() => {
              setFrontImage(null);
              setStep('guide');
            }}
          >
            表面から撮り直す
          </OutlineButton>
        </div>
      </div>
    );
  } else if (step === 'preview') {
    body = (
      <PreviewStep
        type={type}
        frontImage={frontImage}
        backImage={backImage}
        onRetake={() => {
          setFrontImage(null);
          setBackImage(null);
          setStep('guide');
        }}
        onProceed={() => setStep('selfie')}
      />
    );
  } else if (step === 'selfie') {
    body = (
      <div style={{ padding: 36, textAlign: 'center', maxWidth: 560, margin: '0 auto' }}>
        <h2 style={{ fontSize: 18, fontWeight: 'bold' }}>カメラでご自身を撮影しましょう</h2>
        <p style={{ marginTop: 16, fontSize: 14 }}>お顔全体が映るように調整してください。</p>
        <div style={{ marginTop: 24 }}>
          <PrimaryButton onClick={() => selfie.trigger()}>自撮りを撮影する</PrimaryButton>
        </div>
        <div style={{ marginTop: 12 }}>
          <OutlineButton onClick={() => setStep('preview')}>戻る</OutlineButton>
        </div>
      </div>
    );
  } else if (step === 'profile') {
    body = (
      <ProfileStep
        type={type}
        isSubmitting={isSubmittingProfile}
        onSubmit={handleProfileSubmit}
      />
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#fff' }}>
      <style>{IDW_2026_STYLE}</style>
      {body}
      {frontCamera.input}
      {frontLibrary.input}
      {backCamera.input}
      {backLibrary.input}
      {selfie.input}
      <DocumentPickerModal
        isOpen={isPickingDocument}
        onClose={() => setIsPickingDocument(false)}
        onSelect={handleDocumentSelect}
      />
      {isProcessingImage && <LoadingOverlay message="画像を準備しています..." />}
      {isVerifyingFace && <LoadingOverlay message="確認しています..." />}
      {isSubmittingProfile && !alert && <LoadingOverlay message="送信しています..." />}
      <AlertModal alert={alert} onDismiss={handleAlertDismiss} />
    </div>
  );
};

IdentificationWizard.propTypes = {
  redirectUrl: PropTypes.string,
  rulesUrl: PropTypes.string.isRequired,
  privacyPolicyUrl: PropTypes.string.isRequired,
  homeUrl: PropTypes.string.isRequired,
};

IdentificationWizard.defaultProps = {
  redirectUrl: null,
};

export default IdentificationWizard;
